import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, X } from 'lucide-react';
import { updateMetaTags } from '../utils/seo';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');

  useEffect(() => {
    updateMetaTags({
      title: 'Login',
      description: 'Access your Ortus FMCG account to manage orders, dealers, and more.',
      siteName: 'Ortus FMCG'
    });
  }, []);
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/shop');
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const requestReset = async (e) => {
    e.preventDefault();
    setForgotMessage('');
    setForgotLoading(true);
    try {
      await fetch((process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api/v1') + '/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail }),
      });
      setForgotMessage('Reset link sent. Please check your inbox.');
    } catch (err) {
      setForgotMessage('Unable to send reset email. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-slate-950 overflow-hidden">
      <div className="absolute inset-0 opacity-60" style={{ background: 'radial-gradient(circle at top, rgba(16,185,129,0.35), transparent 55%)' }} />
      <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(15,118,110,0.12),rgba(8,47,73,0.65))]" />
      <div className="relative z-10 max-w-6xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="text-white space-y-6">
            <p className="uppercase tracking-[0.35em] text-red-300 text-xs">Member Access</p>
            <h1 className="text-4xl lg:text-5xl font-black leading-tight">Welcome back to the Ortus FMCG ecosystem.</h1>
            <p className="text-slate-300 text-lg">Manage purchases, track wholesale orders, and access dealer tools from a single secure workspace.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {["Unified commerce dashboard","Real-time order tracking","Secure wallet & invoicing","Priority dealer support"].map(feature => (
                <div key={feature} className="bg-white/5 backdrop-blur rounded-2xl border border-white/10 px-5 py-4">
                  <p className="text-sm font-semibold text-red-200">{feature}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-2xl p-8 lg:p-10">
            <div className="mb-8">
              <p className="text-xs font-semibold tracking-[0.4em] text-red-600 uppercase">Secure sign in</p>
              <h2 className="text-3xl font-bold text-slate-900 mt-3">Log in to Ortus</h2>
              <p className="text-sm text-slate-500 mt-2">New to Ortus? <Link to="/register" className="text-red-600 font-semibold">Create an account</Link></p>
            </div>

            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
                {error}
              </div>
            )}

            <form className="space-y-5" onSubmit={handleSubmit}>
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-semibold text-slate-600">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-900 focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-semibold text-slate-600">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    className="w-full rounded-2xl border border-slate-200 px-4 py-3 pr-12 text-slate-900 focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <p className="text-right text-xs text-slate-400">
                  <button type="button" onClick={() => setForgotOpen(true)} className="text-red-600 font-semibold">Forgot password?</button>
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-slate-900 text-white font-semibold py-3 rounded-2xl shadow-lg shadow-red-500/20 hover:bg-slate-800 transition disabled:opacity-60"
              >
                {loading ? 'Signing in...' : 'Access account'}
              </button>
            </form>

            <p className="text-xs text-slate-400 mt-6 text-center">Protected with role-based access & device intelligence.</p>
          </div>
        </div>
      </div>
      {forgotOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 relative">
            <button className="absolute top-4 right-4 text-slate-400 hover:text-slate-600" onClick={() => setForgotOpen(false)}>
              <X size={18} />
            </button>
            <h3 className="text-2xl font-bold text-slate-900 mb-3">Reset your password</h3>
            <p className="text-sm text-slate-500 mb-6">Enter the email associated with your account and we’ll send you a secure reset link.</p>
            {forgotMessage && <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 px-4 py-2 rounded-xl">{forgotMessage}</div>}
            <form className="space-y-4" onSubmit={requestReset}>
              <input
                type="email"
                required
                placeholder="you@company.com"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-900 focus:outline-none focus:ring-2 focus:ring-red-500"
              />
              <button type="submit" disabled={forgotLoading} className="w-full bg-red-600 text-white font-semibold py-3 rounded-2xl hover:bg-red-500 transition disabled:opacity-60">
                {forgotLoading ? 'Sending...' : 'Send reset link'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;
